/**
 * Timezone & Date/Time formatting utility.
 * SINGLE SOURCE OF TRUTH for all date/time display in the app.
 * 
 * Settings are loaded from GET /settings/general which resolves:
 *   User saved settings → Global localization defaults → Hardcoded defaults
 * 
 * Priority chain for timezone:
 *   1. Device timezone override (if enabled on specific device)
 *   2. User timezone setting (per-user account)
 *   3. Global timezone setting (system-wide)
 *   4. Browser timezone (fallback)
 * 
 * IMPORTANT FOR FUTURE DEVELOPMENT:
 *   ALL timestamps in the app MUST use formatDT() or formatDateOnly() from this utility.
 *   NEVER use raw .toLocaleString(), .toLocaleDateString(), or hardcode 'en-AU'.
 *   The date/time format is controlled by General Settings > Date & Time Format.
 * 
 * Usage:
 *   import { resolveTimezone, formatDT, formatDateOnly } from '../utils/timezone';
 *   formatDT('2026-03-19T12:00:00Z')           // → "19/03/2026, 14:00" (using user settings)
 *   formatDateOnly('2026-03-19T12:00:00Z')      // → "19/03/2026"
 *   formatDT(date, { device })                   // → uses device timezone override if enabled
 */

// Cache for settings fetched from API
let _cachedUserTz = null;
let _cachedGlobalTz = null;
let _cachedDateFormat = 'DD/MM/YYYY';
let _cachedTimeFormat = '24h';
let _cachedDistanceUnit = 'km';   // 'km' | 'mi'
let _cachedSpeedUnit = 'kmh';     // 'kmh' | 'mph'
let _cacheLoaded = false;

// ── Intl.DateTimeFormat instance cache (performance) ───────────────────────────
// Date.prototype.toLocaleString(locale, options) internally constructs a NEW
// Intl.DateTimeFormat on EVERY call — and Live Tracking formats timestamps for dozens
// to thousands of device cards on every render. Reusing formatters (keyed by locale +
// options) removes a large, scaling chunk of the React render cost while producing
// byte-identical output. A settings change (date/time/timezone) yields a different key,
// so output is never stale; the map stays tiny (a few timezones × a few option shapes).
const _dtFormatterCache = new Map();
const getDTFormatter = (locale, options) => {
  const key = locale + '|' + JSON.stringify(options);
  let f = _dtFormatterCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, options);
    _dtFormatterCache.set(key, f);
  }
  return f;
};
// Browser timezone never changes during a session — resolve it once.
let _browserTz = null;
const getBrowserTz = () => _browserTz || (_browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone);


/**
 * Load timezone + date/time format settings from the API. Called on login.
 */
export const loadTimezoneSettings = async (api) => {
  try {
    // Load full general settings (includes timezone, date_format, time_format)
    const res = await api.get('/settings/general');
    const data = res.data || {};
    _cachedUserTz = data.timezone || null;
    _cachedDateFormat = data.date_format || 'DD/MM/YYYY';
    _cachedTimeFormat = data.time_format || '24h';
    _cachedDistanceUnit = data.distance_unit || 'km';
    _cachedSpeedUnit = data.speed_unit || 'kmh';
    _cacheLoaded = true;
  } catch {
    // Fallback: try the old timezone-only endpoint
    try {
      const res = await api.get('/settings/timezone');
      _cachedUserTz = res.data?.user_timezone || null;
      _cachedGlobalTz = res.data?.global_timezone || null;
    } catch { /* ignore */ }
    _cacheLoaded = true;
  }
};

/**
 * Get the cached user timezone.
 */
export const getUserTimezone = () => _cachedUserTz;

/**
 * Get the cached global timezone.
 */
export const getGlobalTimezone = () => _cachedGlobalTz;

/**
 * Get the cached date format string.
 */
export const getDateFormat = () => _cachedDateFormat;

/**
 * Get the cached time format string.
 */
export const getTimeFormat = () => _cachedTimeFormat;

/**
 * Manually set locale settings (used by components that load settings independently).
 */
export const setLocaleSettings = ({ dateFormat, timeFormat, timezone, distanceUnit, speedUnit } = {}) => {
  if (dateFormat) _cachedDateFormat = dateFormat;
  if (timeFormat) _cachedTimeFormat = timeFormat;
  if (timezone) _cachedUserTz = timezone;
  if (distanceUnit) _cachedDistanceUnit = distanceUnit;
  if (speedUnit) _cachedSpeedUnit = speedUnit;
};

// ── Distance & speed units ──────────────────────────────────────────────────
// Backend stores all speeds in km/h and all distances in km. These helpers convert
// to the user's configured unit (Settings > Units & Measurement) for DISPLAY only.
const MILES_PER_KM = 0.621371;

export const getDistanceUnit = () => _cachedDistanceUnit;          // 'km' | 'mi'
export const getSpeedUnit = () => _cachedSpeedUnit;                // 'kmh' | 'mph'
export const distanceUnitLabel = () => (_cachedDistanceUnit === 'mi' ? 'mi' : 'km');
export const speedUnitLabel = () => (_cachedSpeedUnit === 'mph' ? 'mph' : 'km/h');

// Human-readable trip duration from seconds, e.g. 2520 -> "42m", 3900 -> "1h 5m".
export const formatDuration = (seconds) => {
  if (seconds == null || seconds < 0) return '';
  const m = Math.round(seconds / 60);
  if (m < 1) return '<1m';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
};

/** Convert a km distance to the user's unit. Returns a Number, or null if invalid. */
export const convertDistance = (km) => {
  const n = Number(km);
  if (!isFinite(n)) return null;
  return _cachedDistanceUnit === 'mi' ? n * MILES_PER_KM : n;
};

/** Convert a km/h speed to the user's unit. Returns a Number, or null if invalid. */
export const convertSpeed = (kmh) => {
  const n = Number(kmh);
  if (!isFinite(n)) return null;
  return _cachedSpeedUnit === 'mph' ? n * MILES_PER_KM : n;
};

/** Format a km distance in the user's unit, e.g. "12.3 km" or "7.6 mi". */
export const formatDistance = (km, { decimals = 1, withUnit = true } = {}) => {
  const v = convertDistance(km);
  if (v === null) return withUnit ? `- ${distanceUnitLabel()}` : '-';
  const num = v.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return withUnit ? `${num} ${distanceUnitLabel()}` : num;
};

/** Format a km/h speed in the user's unit, e.g. "80 km/h" or "50 mph". */
export const formatSpeed = (kmh, { decimals = 0, withUnit = true } = {}) => {
  const v = convertSpeed(kmh);
  if (v === null) return withUnit ? `- ${speedUnitLabel()}` : '-';
  const num = v.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return withUnit ? `${num} ${speedUnitLabel()}` : num;
};

/**
 * Resolve the timezone for display, following the priority chain.
 * @param {Object} options
 * @param {Object} [options.device] - Device object (may have timezone_override_enabled + timezone_override)
 * @param {string} [options.userTimezone] - Override for user timezone (if not using cache)
 * @param {string} [options.globalTimezone] - Override for global timezone (if not using cache)
 * @returns {string} IANA timezone string
 */
export const resolveTimezone = ({ device, userTimezone, globalTimezone } = {}) => {
  // 1. Device effective timezone / override (highest priority)
  if (device?.effective_timezone) {
    return device.effective_timezone;
  }
  if (device?.timezone_override_enabled && device?.timezone_override === 'auto' && device?.timezone_auto) {
    return device.timezone_auto;
  }
  if (device?.timezone_override_enabled && device?.timezone_override && device.timezone_override !== 'auto') {
    return device.timezone_override;
  }

  // 2. User timezone
  const userTz = userTimezone || _cachedUserTz;
  if (userTz) return userTz;

  // 3. Global timezone
  const globalTz = globalTimezone || _cachedGlobalTz;
  if (globalTz && globalTz !== 'UTC') return globalTz;

  // 4. Browser timezone (fallback)
  return getBrowserTz();
};

const getFormatterParts = (date, timeZone) => {
  // NOTE: hour12 must NOT be set together with hourCycle — per the Intl spec,
  // hour12 overrides hourCycle and forces en-CA to h24, which renders midnight
  // as hour "24" and corrupted day-boundary offset math by 24 hours.
  const formatter = getDTFormatter('en-CA', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const parts = formatter.formatToParts(date);
  const out = {};
  for (const part of parts) {
    if (part.type !== 'literal') out[part.type] = Number(part.value);
  }
  return {
    year: out.year,
    month: out.month,
    day: out.day,
    hour: out.hour === 24 ? 0 : out.hour,
    minute: out.minute,
    second: out.second,
  };
};

const getTimeZoneOffsetMs = (date, timeZone) => {
  const parts = getFormatterParts(date, timeZone);
  const zonedUtcTs = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  const actualUtcTs = Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  );
  return zonedUtcTs - actualUtcTs;
};

export const getZonedParts = (dateInput, options = {}) => {
  if (!dateInput) return null;
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(date.getTime())) return null;
  const tz = options.timezone || resolveTimezone({ device: options.device });
  return getFormatterParts(date, tz);
};

export const zonedDateTimeToUtc = (parts, timezone) => {
  if (!parts || !timezone) return null;
  const year = Number(parts.year);
  const month = Number(parts.month);
  const day = Number(parts.day);
  const hour = Number(parts.hour || 0);
  const minute = Number(parts.minute || 0);
  const second = Number(parts.second || 0);
  if (!year || !month || !day) return null;

  let utcTs = Date.UTC(year, month - 1, day, hour, minute, second);
  for (let i = 0; i < 3; i += 1) {
    const offsetMs = getTimeZoneOffsetMs(new Date(utcTs), timezone);
    const nextUtcTs = Date.UTC(year, month - 1, day, hour, minute, second) - offsetMs;
    if (Math.abs(nextUtcTs - utcTs) < 1000) {
      utcTs = nextUtcTs;
      break;
    }
    utcTs = nextUtcTs;
  }
  return new Date(utcTs);
};

export const getTimeValueInTimezone = (dateInput, options = {}) => {
  const parts = getZonedParts(dateInput, options);
  if (!parts) return '00:00';
  return `${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`;
};

/**
 * Get the locale string based on the configured date format.
 * DD/MM/YYYY → en-AU, MM/DD/YYYY → en-US, YYYY-MM-DD → en-CA.
 * NOTE: en-CA (not sv-SE) is used for YYYY-MM-DD so the date order is ISO while the
 * AM/PM markers stay English ("a.m."/"p.m."). sv-SE rendered them as "fm"/"em" (Swedish).
 */
const getLocale = () => {
  switch (_cachedDateFormat) {
    case 'MM/DD/YYYY': return 'en-US';
    case 'YYYY-MM-DD': return 'en-CA';
    default: return 'en-AU'; // DD/MM/YYYY
  }
};

/**
 * Explicit hour cycle for the configured time format. Using hourCycle (instead of the
 * `hour12` boolean) is deterministic across locales — e.g. en-CA defaults to h24 and
 * would otherwise render midnight as "24:00" rather than "00:00".
 */
const getHourCycle = () => (_cachedTimeFormat === '12h' ? 'h12' : 'h23');

/**
 * Normalize the locale's day-period token to a consistent uppercase "AM"/"PM" (no
 * periods), so the style is identical regardless of the date-format locale
 * (en-AU "am", en-US "AM", en-CA "a.m." → all "AM"/"PM"). 24h strings are unaffected.
 */
const normalizeDayPeriod = (str) => str.replace(/([AaPp])\.?\s?[Mm]\.?/g, (_m, p) => `${p.toUpperCase()}M`);


const parseWallClockParts = (dateInput) => {
  if (!dateInput) return null;
  if (dateInput instanceof Date) {
    return {
      year: dateInput.getFullYear(),
      month: dateInput.getMonth() + 1,
      day: dateInput.getDate(),
      hour: dateInput.getHours(),
      minute: dateInput.getMinutes(),
      second: dateInput.getSeconds(),
    };
  }
  const match = String(dateInput).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?/);
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4] || 0),
    minute: Number(match[5] || 0),
    second: Number(match[6] || 0),
  };
};


export const formatWallDT = (dateInput, options = {}) => {
  const parts = parseWallClockParts(dateInput);
  if (!parts || !parts.year || !parts.month || !parts.day) return '-';
  try {
    const date = new Date(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second || 0);
    const fmt = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: getHourCycle(),
    };
    if (options.seconds) fmt.second = '2-digit';
    return normalizeDayPeriod(getDTFormatter(getLocale(), fmt).format(date));
  } catch {
    return String(dateInput);
  }
};


export const formatWallTimeOnly = (dateInput, options = {}) => {
  const parts = parseWallClockParts(dateInput);
  if (!parts) return '-';
  try {
    const date = new Date(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second || 0);
    const fmt = {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: getHourCycle(),
    };
    if (options.seconds) fmt.second = '2-digit';
    return normalizeDayPeriod(getDTFormatter(getLocale(), fmt).format(date));
  } catch {
    return String(dateInput);
  }
};

/**
 * Format a plain calendar date (e.g. "2000-01-01" DOB / licence expiry) in the user's
 * configured date format WITHOUT any timezone conversion — the value is a wall date,
 * so converting via UTC could shift it a day in negative-offset timezones.
 */
export const formatWallDateOnly = (dateInput) => {
  const parts = parseWallClockParts(dateInput);
  if (!parts || !parts.year || !parts.month || !parts.day) return '-';
  try {
    const date = new Date(parts.year, parts.month - 1, parts.day);
    return getDTFormatter(getLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
  } catch {
    return String(dateInput);
  }
};

/**
 * Format a date/time string using the user's configured settings.
 * This is the PRIMARY function to use for ALL timestamp display.
 * 
 * @param {string|Date} dateInput - UTC date string or Date object
 * @param {Object} [options] - Optional overrides
 * @param {Object} [options.device] - Device object for timezone override
 * @param {string} [options.timezone] - Direct timezone override
 * @returns {string} Formatted date+time string
 */
export const formatDT = (dateInput, options = {}) => {
  if (!dateInput) return '-';
  try {
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (isNaN(date.getTime())) return '-';
    const tz = options.timezone || resolveTimezone({ device: options.device });
    const fmt = {
      timeZone: tz,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: getHourCycle(),
    };
    if (options.seconds) fmt.second = '2-digit';
    return normalizeDayPeriod(getDTFormatter(getLocale(), fmt).format(date));
  } catch {
    return String(dateInput);
  }
};

/**
 * Format just the date portion using the user's configured settings.
 * 
 * @param {string|Date} dateInput - UTC date string or Date object
 * @param {Object} [options] - Optional overrides
 * @param {Object} [options.device] - Device object for timezone override
 * @param {string} [options.timezone] - Direct timezone override
 * @returns {string} Formatted date-only string
 */
export const formatDateOnly = (dateInput, options = {}) => {
  if (!dateInput) return '-';
  try {
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (isNaN(date.getTime())) return '-';
    const tz = options.timezone || resolveTimezone({ device: options.device });
    return getDTFormatter(getLocale(), {
      timeZone: tz,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  } catch {
    return String(dateInput);
  }
};

/**
 * Format just the time portion using the user's configured settings.
 * 
 * @param {string|Date} dateInput - UTC date string or Date object
 * @param {Object} [options] - Optional overrides
 * @returns {string} Formatted time-only string
 */
export const formatTimeOnly = (dateInput, options = {}) => {
  if (!dateInput) return '-';
  try {
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (isNaN(date.getTime())) return '-';
    const tz = options.timezone || resolveTimezone({ device: options.device });
    const fmt = {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: getHourCycle(),
    };
    if (options.seconds) fmt.second = '2-digit';
    return normalizeDayPeriod(getDTFormatter(getLocale(), fmt).format(date));
  } catch {
    return String(dateInput);
  }
};

// Compact relative "time ago" (e.g. "3h ago", "5m ago", "2d ago"). The elapsed delta is
// timezone-independent; use formatDT for the absolute time tooltip in the user's timezone.
export const formatTimeAgo = (dateInput) => {
  if (!dateInput) return '';
  try {
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (isNaN(date.getTime())) return '';
    const secs = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
    if (secs < 60) return 'just now';
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    const weeks = Math.floor(days / 7);
    if (weeks < 5) return `${weeks}w ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  } catch {
    return '';
  }
};


// ── Legacy backward-compatible exports ──

/**
 * @deprecated Use formatDT() instead. Kept for backward compatibility.
 */
export const formatDateTime = (dateInput, timezone, options = {}) => {
  return formatDT(dateInput, { timezone, ...options });
};

/**
 * @deprecated Use formatDateOnly() instead. Kept for backward compatibility.
 */
export const formatDate = (dateInput, timezone) => {
  return formatDateOnly(dateInput, { timezone });
};

/**
 * @deprecated Use formatTimeOnly() instead. Kept for backward compatibility.
 */
export const formatTime = (dateInput, timezone) => {
  return formatTimeOnly(dateInput, { timezone });
};

/**
 * Get a filename-safe timestamp in the resolved timezone.
 */
export const getFileTimestamp = (timezone) => {
  const tz = timezone || resolveTimezone();
  const m = new Date().toLocaleString('en-AU', {
    timeZone: tz,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).match(/(\d{2})\/(\d{2})\/(\d{4}), (\d{2}):(\d{2})/);
  if (!m) return new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
  const [, dd, mo, yyyy, HH, MM] = m;
  // File-safe date in the user's configured date format (slashes → dashes).
  const df = _cachedDateFormat || 'DD/MM/YYYY';
  const datePart = df === 'MM/DD/YYYY' ? `${mo}-${dd}-${yyyy}` : df === 'YYYY-MM-DD' ? `${yyyy}-${mo}-${dd}` : `${dd}-${mo}-${yyyy}`;
  let timePart = `${HH}${MM}`;
  if (_cachedTimeFormat === '12h') {
    const h = Number(HH);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    timePart = `${String(h12).padStart(2, '0')}${MM}${ampm}`;
  }
  return `${datePart}_${timePart}`;
};

// Format a plain calendar date string ("YYYY-MM-DD", no time component) in the user's
// configured date format WITHOUT timezone conversion — for licence expiry, DOB, etc.
export const formatPlainDate = (dateStr) => {
  const s = String(dateStr || '').trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return s;
  const [, y, mo, d] = m;
  const df = _cachedDateFormat || 'DD/MM/YYYY';
  if (df === 'MM/DD/YYYY') return `${mo}/${d}/${y}`;
  if (df === 'YYYY-MM-DD') return `${y}-${mo}-${d}`;
  return `${d}/${mo}/${y}`;
};
